import React, { memo, useEffect, useMemo, type FC, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';
import { useLazyQuery } from '@apollo/react-hooks';
import debounce from 'lodash/debounce';

import { N500 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Tooltip from '@atlaskit/tooltip';
import ClockIconMigration from '@atlaskit/icon/core/migration/clock--emoji-frequent';
import { Flex, xcss } from '@atlaskit/primitives';
import { useSharedPluginState } from '@atlaskit/editor-common/hooks';

import { CONTEXT_PATH } from '@confluence/named-routes';
import { ProfileLink } from '@confluence/profile';
import { Link } from '@confluence/route-manager';
import { ErrorDisplay, isUnauthorizedError } from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';
import { useSessionData } from '@confluence/session-data';

import type { UserShape, FullPageEditorPresetAPI } from './byLineTypes';
import { ByLineLastModifiedQuery } from './ByLineLastModifiedQuery.graphql';
import type {
	ByLineLastModifiedQuery as ByLineLastModifiedQueryType,
	ByLineLastModifiedQueryVariables,
} from './__types__/ByLineLastModifiedQuery';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledVersionComment = styled.span({
	display: 'inline-block',
	marginLeft: token('space.075', '6px'),
	cursor: 'default',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:before': {
		content: '•',
		display: 'inline-block',
		color: token('color.text.subtle', 'grey'),
		padding: `0 ${token('space.100', '8px')} 0 0`,
		/* for IE <= 11 */
		textDecoration: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledLastModified = styled.div({
	display: 'inline-block',
	'&::first-letter': {
		textTransform: 'capitalize',
	},
});

const livePageLastUpdatedStyle = xcss({
	marginTop: 'space.025',
	marginLeft: 'space.100',
	marginRight: 'space.150',
});

export type ByLineLastModifiedProps = {
	author: UserShape;
	version: {
		friendlyWhen: string;
		number: number;
		by: UserShape;
		contentTypeModified: boolean;
		when?: string;
	};
	versionComment?: string;
	contentId: string;
	isLivePage?: boolean;
	editorApi?: FullPageEditorPresetAPI | null;
};

export const ByLineLastModified: FC<ByLineLastModifiedProps> = memo(
	({ version, author, contentId, versionComment, isLivePage, editorApi }) => {
		const { collabEditState } = useSharedPluginState(editorApi, ['collabEdit']);
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { locale } = useSessionData();

		const [getByLineLastModified, { data, error, loading }] = useLazyQuery<
			ByLineLastModifiedQueryType,
			ByLineLastModifiedQueryVariables
		>(ByLineLastModifiedQuery, {
			variables: {
				contentId,
				status: ['draft'], // fetching draft status to decouple from Snapshot V2 feature
			},
			fetchPolicy: 'cache-and-network',
		});
		const latestVersion = data?.content?.nodes?.[0]?.version ?? version;

		// Fetch that is immediately invoked before the debounced wait period
		// Used on visibility change (when user switches to this tab) and mouseenter
		const debouncedLeadingFetch = useMemo(
			() =>
				debounce(getByLineLastModified, 60000, {
					leading: true,
					trailing: false,
					maxWait: 2 * 60000,
				}),
			[getByLineLastModified],
		);

		useEffect(() => {
			// Fetch last updated time when tab is focused
			const handleVisibilityChange = async () => {
				if (document.visibilityState === 'visible') {
					debouncedLeadingFetch(); // Get latest value when user switches back to this tab
				}
			};
			if (isLivePage) {
				window.addEventListener('visibilitychange', handleVisibilityChange);
				return () => {
					window.removeEventListener('visibilitychange', handleVisibilityChange);
				};
			}
		}, [debouncedLeadingFetch, isLivePage]);

		// Fetch that is first debounced for the wait period, and then invoked
		// Used on page edit
		const debouncedTrailingFetch = useMemo(
			() => debounce(getByLineLastModified, 30000, { maxWait: 60000 }),
			[getByLineLastModified],
		);

		// Update time stamp on page edit
		useEffect(() => {
			if (
				collabEditState?.initialised?.lastRemoteOrganicBodyChangeAt ||
				collabEditState?.initialised?.lastLocalOrganicBodyChangeAt
			) {
				debouncedTrailingFetch();
			}
		}, [
			debouncedTrailingFetch,
			collabEditState?.initialised?.lastRemoteOrganicBodyChangeAt,
			collabEditState?.initialised?.lastLocalOrganicBodyChangeAt,
		]);

		// Cancel any pending debounces on unmount
		useEffect(() => {
			return () => {
				debouncedLeadingFetch.cancel();
				debouncedTrailingFetch.cancel();
			};
		}, [debouncedLeadingFetch, debouncedTrailingFetch]);

		// Medium dateStyle does not support 'at' combiner, so we're using separate date/time formatters
		const dayFormatter = Intl.DateTimeFormat(locale, { dateStyle: 'medium' });
		const timeFormatter = Intl.DateTimeFormat(locale, { timeStyle: 'short' });
		const tooltipContent = () => {
			if (loading) {
				return null;
			} else if (latestVersion.when) {
				const date = new Date(latestVersion.when);
				return (
					<FormattedMessage
						id="content-header.by-line.live-pages.last.updated.tooltip"
						defaultMessage="Last updated: {day} at {time}"
						description="Tooltip content for last updated date"
						values={{
							day: dayFormatter.format(date),
							time: timeFormatter.format(date),
						}}
					/>
				);
			}
		};

		let lastUpdatedLine: ReactNode;

		if (isLivePage) {
			if (error) {
				if (isUnauthorizedError(error)) {
					markErrorAsHandled(error);
					return null;
				} else {
					// TODO: unknown errors should have a nicer UI within `<ErrorDisplay>`, but we don't have such UI for now
					// so returning empty `<ErrorDisplay>` here.
					return <ErrorDisplay error={error} />;
				}
			}

			lastUpdatedLine = latestVersion.friendlyWhen;

			return (
				<Tooltip content={tooltipContent()} onShow={debouncedLeadingFetch}>
					<Flex
						alignItems="center"
						gap="space.025"
						xcss={livePageLastUpdatedStyle}
						testId="live-page-last-updated"
					>
						<ClockIconMigration
							label="Last updated"
							LEGACY_size="small"
							color={token('color.icon')}
							testId="last-updated-icon"
						/>
						{/* TODO: add link to page version comparison */}
						{lastUpdatedLine}
					</Flex>
				</Tooltip>
			);
		} else if (version.number === 1 || version.contentTypeModified) {
			lastUpdatedLine = (
				<span id="content-header.by-line.last.updated.version.1">{version.friendlyWhen}</span>
			);
		} else {
			const modifiedByAuthor = !author.isAnonymous && author.userId === version.by.userId;
			const currentVersion = version.number;
			const prevVersion = currentVersion - 1;
			const query = `pageId=${contentId}&selectedPageVersions=${prevVersion}&selectedPageVersions=${currentVersion}`;
			const style = {
				color: token('color.text.subtle', N500),
			};
			const changeLink = (
				<Link
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={style}
					href={`${CONTEXT_PATH}/pages/diffpagesbyversion.action?${query}`}
				>
					{version.friendlyWhen}
				</Link>
			);

			lastUpdatedLine = modifiedByAuthor ? (
				<FormattedMessage
					id="content-header.by-line.last.updated.self.expanded"
					defaultMessage="Last updated: {date}"
					description="Text to show Last updated label"
					values={{
						date: changeLink,
					}}
				/>
			) : (
				<FormattedMessage
					id="content-header.by-line.last.updated.expanded"
					defaultMessage="Last updated: {date} by {contributor}"
					description="Text to show Last updated Date by contributor label"
					values={{
						date: changeLink,
						contributor: (
							<ProfileLink
								userId={version.by.userId}
								fullName={version.by.fullName}
								isAnonymous={version.by.isAnonymous}
								referralSource="profilecard"
							/>
						),
					}}
				/>
			);

			if (versionComment) {
				const fireTooltipEvent = () => {
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							source: 'page',
							action: 'displayed',
							actionSubject: 'tooltip',
							actionSubjectId: 'publishComment',
						},
					}).fire();
				};

				lastUpdatedLine = (
					<span>
						{lastUpdatedLine}
						<Tooltip tag="span" content={versionComment} onShow={fireTooltipEvent}>
							<StyledVersionComment>
								<FormattedMessage
									id="content-header.by-line.last.updated.version.comment.indicator"
									defaultMessage="Version comment"
									description="Text that indicates a comment was made the last time this page was published. Rolling over the text shows the comment."
								/>
							</StyledVersionComment>
						</Tooltip>
					</span>
				);
			}
		}

		return (
			<StyledLastModified data-testid="page-last-modified">{lastUpdatedLine}</StyledLastModified>
		);
	},
);
